import { supabase } from './supabase';

export interface MapboxSuggestion {
  mapbox_id: string;
  name: string;
  full_address: string;
  address: string;
  place_formatted: string;
  feature_type: string;
  context: any;
}

export interface MapboxRetrievedAddress {
  address_line_1: string;
  address_line_2: string;
  suburb: string;
  city: string;
  postcode: string;
  full_address: string;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Call the mapbox-address edge function for address suggestions.
 * The edge function holds the Mapbox API key server-side (reads from
 * MAPBOX_TOKEN env var, with fallback to MAPBOX_ACCESS_TOKEN or the settings table).
 */

export async function mapboxSuggest(
  query: string,
  sessionToken: string
): Promise<MapboxSuggestion[]> {
  if (!query || query.length < 2) return [];

  const { data, error } = await supabase.functions.invoke('mapbox-address', {
    body: {
      action: 'suggest',
      query,
      session_token: sessionToken,
    },
  });

  if (error) {
    console.error('Mapbox suggest error:', error);
    throw error;
  }

  if (data?.error) {
    console.error('Mapbox suggest API error:', data.error);
    throw new Error(data.error);
  }

  return data?.suggestions || [];
}

/**
 * Call the mapbox-address edge function to retrieve full address details
 * for a selected suggestion. Falls back to parsing the raw Mapbox response
 * if the edge function returns it in a different shape.
 */
export async function mapboxRetrieve(
  mapboxId: string,
  sessionToken: string
): Promise<MapboxRetrievedAddress> {
  const { data, error } = await supabase.functions.invoke('mapbox-address', {
    body: {
      action: 'retrieve',
      mapbox_id: mapboxId,
      session_token: sessionToken,
    },
  });

  if (error) {
    console.error('Mapbox retrieve error:', error);
    throw error;
  }

  if (data?.error) {
    console.error('Mapbox retrieve API error:', data.error);
    throw new Error(data.error);
  }

  // Mapbox returns coordinates as [lng, lat] (GeoJSON order) -- flip to the
  // {latitude, longitude} shape the rest of the app (and the IVR) expects.
  const coordsToLatLng = (coords: unknown): { latitude: number | null; longitude: number | null } => {
    if (Array.isArray(coords) && coords.length === 2) {
      return { longitude: Number(coords[0]), latitude: Number(coords[1]) };
    }
    return { latitude: null, longitude: null };
  };

  // The edge function may return the address in data.address (our custom shape)
  // or it may return the raw Mapbox retrieve response in data.features
  const addr = data?.address;
  if (addr) {
    return {
      address_line_1: addr.address_line_1 || addr.name || '',
      address_line_2: addr.address_line_2 || '',
      suburb: addr.suburb || '',
      city: addr.city || '',
      postcode: addr.postcode || '',
      full_address: addr.full_address || '',
      ...coordsToLatLng(addr.coordinates),
    };
  }

  // Fallback: try to parse from raw Mapbox retrieve response
  // The Mapbox Search Box API retrieve returns { type: "FeatureCollection", features: [...] }
  const features = data?.features;
  if (features && features.length > 0) {
    const feature = features[0];
    const props = feature.properties || {};
    const ctx = props.context || {};

    // Build address_line_1 from name or address
    const addressLine1 = props.name || props.address || props.full_address?.split(',')[0] || '';

    // Extract components from context
    const suburb = ctx.locality?.name || ctx.neighborhood?.name || ctx.place?.name || '';
    const city = ctx.region?.name || ctx.district?.name || '';
    const postcode = ctx.postcode?.name || '';
    const fullAddress = props.full_address || props.place_formatted || '';

    return {
      address_line_1: addressLine1,
      address_line_2: '',
      suburb,
      city,
      postcode,
      full_address: fullAddress,
      ...coordsToLatLng(feature.geometry?.coordinates),
    };
  }

  // Last resort: try to use data directly if it has address-like fields
  if (data?.name || data?.full_address) {
    return {
      address_line_1: data.name || data.full_address?.split(',')[0] || '',
      address_line_2: '',
      suburb: data.context?.locality?.name || data.context?.place?.name || '',
      city: data.context?.region?.name || '',
      postcode: data.context?.postcode?.name || '',
      full_address: data.full_address || data.place_formatted || '',
      ...coordsToLatLng(data.geometry?.coordinates),
    };
  }

  throw new Error('No address returned from Mapbox retrieve');
}
